import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/contexts/axiosApiInstance";
import type { SearchResponse } from "./types";

// Debounce window for keystrokes. 180ms is fast enough to feel live but
// coalesces typical typing bursts. Re-tune only with evidence.
const DEBOUNCE_MS = 180;

// Axios timeout. Pg_trgm + LIKE search p99 is well under 50ms; 8s is the
// "something is fundamentally wrong" threshold that surfaces as a distinct
// "Search timed out" error rather than spinning forever.
const TIMEOUT_MS = 8000;

// Tiny LRU keyed by query string. Eliminates flicker when a user types
// "kg" → deletes → retypes "kg".
const LRU_SIZE = 10;

export type UnitSearchState =
  | { status: "idle"; result: SearchResponse | null }
  | { status: "loading"; result: SearchResponse | null }
  | { status: "error"; error: string; result: SearchResponse | null };

interface UseUnitSearchArgs {
  query: string;
  quantityKindUri?: string | null;
}

interface LruEntry {
  key: string;
  value: SearchResponse;
}

function lruGet(cache: LruEntry[], key: string): SearchResponse | null {
  const idx = cache.findIndex((e) => e.key === key);
  if (idx === -1) return null;
  // Move to front
  const [hit] = cache.splice(idx, 1);
  cache.unshift(hit);
  return hit.value;
}

function lruSet(cache: LruEntry[], key: string, value: SearchResponse): void {
  const idx = cache.findIndex((e) => e.key === key);
  if (idx !== -1) cache.splice(idx, 1);
  cache.unshift({ key, value });
  if (cache.length > LRU_SIZE) cache.pop();
}

export function useUnitSearch({
  query,
  quantityKindUri,
}: UseUnitSearchArgs): UnitSearchState {
  const [state, setState] = useState<UnitSearchState>({
    status: "idle",
    result: null,
  });
  const cacheRef = useRef<LruEntry[]>([]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setState({ status: "idle", result: null });
      return;
    }

    const cacheKey = `${quantityKindUri ?? ""}::${trimmed}`;
    const cached = lruGet(cacheRef.current, cacheKey);
    if (cached) {
      setState({ status: "idle", result: cached });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState((prev) => ({ status: "loading", result: prev.result }));

    const timer = setTimeout(() => {
      axiosInstance
        .get<SearchResponse>("/api/units/search", {
          params: {
            q: trimmed,
            ...(quantityKindUri ? { quantity_kind: quantityKindUri } : {}),
          },
          signal: controller.signal,
          timeout: TIMEOUT_MS,
        })
        .then((res) => {
          if (cancelled) return;
          lruSet(cacheRef.current, cacheKey, res.data);
          setState({ status: "idle", result: res.data });
        })
        .catch((err) => {
          if (cancelled || controller.signal.aborted) return;
          const isTimeout =
            err?.code === "ECONNABORTED" || err?.message?.includes("timeout");
          const msg = isTimeout ? "Search timed out" : "Search failed";
          setState((prev) => ({
            status: "error",
            error: msg,
            result: prev.result,
          }));
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, quantityKindUri]);

  return state;
}
